import { DocumentPaperSize, NumberLimit } from "@/Contracts";
import {
	ProductBillingType,
	productBillingTypeOptions
} from "@/Contracts/Products";
import { PX_PER_MM } from "@/Contracts/ProposalBuilder";
import { DragEndResult } from "@/Contracts/ProposalBuilder";
import {
	BlockMapper,
	BlockType,
	EditQuantityRange,
	QuoteLabels
} from "@/Contracts/ProposalBuilder/Block";
import { DropResult } from "react-beautiful-dnd";
import { capitalizeFirstLetter } from "../common";
import { Doc } from "prettier";

export const formPageBasedBlocks = (
	blockList: string[],
	blockMapper: BlockMapper
): string[][] => {
	const pageBasedBlocks: string[][] = [];
	let blocksPerPage: string[] = [];

	blockList.forEach((blockId, index) => {
		if (blockMapper[blockId]?.type === BlockType.PAGE_BREAK) {
			blocksPerPage.push(blockId);
			pageBasedBlocks.push(blocksPerPage);
			blocksPerPage = [];
			return;
		}

		if (blockMapper[blockId]) {
			blocksPerPage.push(blockId);
		}

		if (index === blockList.length - 1) {
			pageBasedBlocks.push(blocksPerPage);
		}
	});

	return pageBasedBlocks;
};

export const getDragEndResult = (
	blockList: string[],
	blockMapper: BlockMapper,
	result: DropResult
): DragEndResult => {
	const pageBasedBlocks = formPageBasedBlocks(blockList, blockMapper);

	const { source, destination } = result;

	const destinationPageNumber = Number(
		destination?.droppableId.split("::")[1]
	);
	const sourcePageNumber = Number(source?.droppableId.split("::")[1]);

	if (
		isNaN(destinationPageNumber) ||
		isNaN(sourcePageNumber) ||
		!destination
	) {
		console.warn("Invalid destination page number");
		return {
			destinationBlockId: null,
			sourceBlockId: null,
			destinationPageBlocks: [],
			sourcePageBlocks: [],
			destinationPageNumber: 0,
			sourcePageNumber: 0
		};
	}

	const destinationPageBlocks = pageBasedBlocks[destinationPageNumber];
	const sourcePageBlocks = pageBasedBlocks[sourcePageNumber];

	const destinationBlockId = destinationPageBlocks[destination.index];
	const sourceBlockId = sourcePageBlocks[source.index];

	return {
		destinationBlockId,
		sourceBlockId,
		destinationPageBlocks,
		sourcePageBlocks,
		destinationPageNumber,
		sourcePageNumber
	};
};

export const addBlockToDocument = (
	blockList: string[],
	blockMapper: BlockMapper,
	result: DropResult
): number | undefined => {
	const { destination } = result;

	if (!destination) {
		return;
	}

	const pageBasedBlocks = formPageBasedBlocks(blockList, blockMapper);

	const pageNumber = Number(result.destination?.droppableId.split("::")[1]);

	if (isNaN(pageNumber)) {
		console.warn("Invalid page number");
		return;
	}

	const pageBlocks = pageBasedBlocks[pageNumber];

	if (!pageBlocks && !pageBasedBlocks.length) {
		return destination.index;
	}

	const currentPresentBlock = pageBlocks[destination.index];
	const flatBlocksIndex = blockList.findIndex(
		blockId => blockId === currentPresentBlock
	);

	if (flatBlocksIndex < 0) {
		// adding block to a new page other than first page
		const blocksAbove = [
			...pageBasedBlocks?.slice(0, pageNumber),
			...pageBlocks
		].flat();

		return blocksAbove.length;
	}

	return flatBlocksIndex;
};

export const removeEstimatedPageBreaks = (index: number) => {
	const existingPageBreaks: Element[] = document?.querySelectorAll(
		`.estimated-page-break-${index}`
	) as unknown as Element[];

	existingPageBreaks.forEach(pageBreak => {
		pageBreak.parentNode?.removeChild(pageBreak);
	});
};

const createAndAppendPageBreakElement = (
	index: number,
	pageTop: number,
	currentPage: Element
) => {
	const currentEstimatedPageBreak = document.createElement("div");
	currentEstimatedPageBreak.style.position = "absolute";
	currentEstimatedPageBreak.style.right = "-11rem";
	currentEstimatedPageBreak.className = `estimated-page-break-${index}`;
	currentEstimatedPageBreak.innerHTML = `<div class="flex space-x-4 relative">
			<div>----</div>
			<div class="flex flex-col items-center relative -top-2">
				<div>Estimated</div> <div>page break</div>
			</div>
		</div>`;
	currentEstimatedPageBreak.style.top = `${pageTop}px`;
	currentPage.appendChild(currentEstimatedPageBreak);
};

const getPaperSizeHeight = (paperSize: DocumentPaperSize): number => {
	switch (paperSize) {
		case DocumentPaperSize.A4:
			return Math.floor(PX_PER_MM * 297);
		case DocumentPaperSize.Letter:
			return Math.floor(PX_PER_MM * 279);
		default:
			throw new Error("Invalid Paper Size");
	}
};

export const calculateEstimatedPageBreaks = (
	index: number,
	isHeadersFootersActive: boolean,
	paperSize: DocumentPaperSize
) => {
	const a4heightPx = getPaperSizeHeight(paperSize);
	const pageTopPadding = isHeadersFootersActive ? 72 : 56;
	const topBottomPadding = pageTopPadding * 2;
	const a4ContentSize = a4heightPx - topBottomPadding;

	const currentPage = document.querySelector(`#document-page-${index}`);
	let currentPageHeight = Math.floor(
		currentPage?.getBoundingClientRect().height ?? 0
	);

	if (!currentPageHeight) {
		return;
	}

	if (!isHeadersFootersActive) {
		currentPageHeight -= topBottomPadding;
	}

	const estimatedPageBreaks = Math.floor(currentPageHeight / a4ContentSize);

	removeEstimatedPageBreaks(index);

	if (
		!currentPage ||
		currentPageHeight === a4ContentSize ||
		estimatedPageBreaks < 1
	) {
		return;
	}

	[...new Array(estimatedPageBreaks)].forEach((_pageBreak, pageNumber) => {
		const pageTop =
			a4ContentSize * (1 + pageNumber) +
			(isHeadersFootersActive ? 0 : pageTopPadding);
		createAndAppendPageBreakElement(index, pageTop - 11, currentPage);
	});
};

export const calculateAccurateEstimatedPageBreaks = (
	index: number,
	isHeadersFootersActive: boolean,
	paperSize: DocumentPaperSize
): void => {
	const a4heightPx = getPaperSizeHeight(paperSize);
	const pageTopPadding = isHeadersFootersActive ? 72 : 56;
	const topBottomPadding = pageTopPadding * 2;
	const a4ContentSize = a4heightPx - topBottomPadding;

	const currentPage = document.querySelector(`#document-page-${index}`);
	let currentPageHeight = Math.floor(
		currentPage?.getBoundingClientRect().height ?? 0
	);
	const currentPageTop = currentPage?.getBoundingClientRect()?.top ?? 0;

	if (!currentPageHeight) {
		return;
	}

	if (!isHeadersFootersActive) {
		currentPageHeight -= topBottomPadding;
	}

	const estimatedPageBreaks = Math.floor(currentPageHeight / a4ContentSize);

	removeEstimatedPageBreaks(index);

	if (
		!currentPage ||
		currentPageHeight === a4ContentSize ||
		estimatedPageBreaks < 1
	) {
		return;
	}

	const prohibitedElementsForPageBreak = currentPage?.querySelectorAll(
		".image-block.parent-block-container, .video-block.parent-block-container, .signature-block.parent-block-container, .quote-product__name, .quote-product__images, .quote-block-footer, .quote-section__header, div.mce-content-body > h1, h2, h3, h4, h5, h6, .risk-score-component"
	);

	const allEstimatedPageBreaks: number[] = [
		...new Array(estimatedPageBreaks)
	].map((_pageBreak, pageNumber) => {
		return (
			a4ContentSize * (1 + pageNumber) +
			(isHeadersFootersActive ? 0 : pageTopPadding)
		);
	});

	if (!!prohibitedElementsForPageBreak?.length) {
		let newEstimatedPageBreaks: number[] = allEstimatedPageBreaks;
		//mapping over all the image and video blocks
		prohibitedElementsForPageBreak.forEach(block => {
			newEstimatedPageBreaks = getNewEstimatedPageBreaks(
				block,
				newEstimatedPageBreaks,
				currentPageTop
			);
		});

		newEstimatedPageBreaks.forEach(pageBreak => {
			createAndAppendPageBreakElement(index, pageBreak - 11, currentPage);
		});
	} else {
		[...new Array(estimatedPageBreaks)].forEach(
			(_pageBreak, pageNumber) => {
				const pageTop =
					a4ContentSize * (1 + pageNumber) +
					(isHeadersFootersActive ? 0 : pageTopPadding);
				createAndAppendPageBreakElement(
					index,
					pageTop - 11,
					currentPage
				);
			}
		);
	}
};

const getNewEstimatedPageBreaks = (
	block: Element,
	allEstimatedPageBreaks: number[],
	currentPageTop: number
): number[] => {
	const newEstimatedPageBreaks = [...allEstimatedPageBreaks];

	// getting the block dimensions
	const blockRect = block?.getBoundingClientRect();
	const blockTop = Math.floor(blockRect?.top - currentPageTop) ?? 0;
	const blockBottom = Math.floor(blockRect?.bottom - currentPageTop) ?? 0;

	if (blockTop < 0 || blockBottom < 0) {
		return newEstimatedPageBreaks;
	}

	//checking if there is a estimated page break which is inside the image or video block
	const interferingPageBreakIndex =
		allEstimatedPageBreaks?.findIndex(
			pageBreak => pageBreak > blockTop && pageBreak < blockBottom
		) ?? -1;
	// if the above condition is not satisfied we should not change the page break thats why making the value to -1;

	if (interferingPageBreakIndex >= 0) {
		//getting the remaining margin of the block
		const remainingMargin =
			allEstimatedPageBreaks[interferingPageBreakIndex] - blockTop;

		//replacing the interfering page break with block top
		newEstimatedPageBreaks[interferingPageBreakIndex] = blockTop;

		//subtracting the remaining margin from all the later page breaks
		for (
			let i = interferingPageBreakIndex + 1;
			i < allEstimatedPageBreaks.length;
			i++
		) {
			const newPageBreak = allEstimatedPageBreaks[i] - remainingMargin;
			//setting the new page breaks after subtracting the remaining margin from it
			newEstimatedPageBreaks[i] = newPageBreak;
		}
	}
	return newEstimatedPageBreaks;
};

export const getBlockName = (bType: BlockType) => {
	const blockType = bType;

	switch (blockType) {
		case BlockType.GRID:
			return "Grid";

		case BlockType.QUOTE:
			return "Quote";

		default:
			return capitalizeFirstLetter(blockType?.split("_")?.join(" "));
	}
};

export const isBlockReadOnly = (
	blockType: BlockType,
	editableBlocks: BlockType[]
) => {
	return !editableBlocks.includes(blockType);
};

export const fileReader = (file: any) => {
	const fileReader = new FileReader();

	return new Promise((resolve, reject) => {
		fileReader.onerror = () => {
			fileReader.abort();
			reject(new Error("Problem parsing file"));
		};
		fileReader.onload = () => {
			resolve(fileReader.result);
		};
		fileReader.readAsText(file);
	});
};

export const getNativeLanguageLabels = (
	billingType: ProductBillingType,
	labels?: QuoteLabels
): string => {
	switch (billingType) {
		case ProductBillingType.Annual:
			return (
				labels?.annually_recurring_label ??
				`${
					productBillingTypeOptions[ProductBillingType.Annual]
				} Recurring`
			);
		case ProductBillingType.Monthly:
			return (
				labels?.monthly_recurring_label ??
				`${
					productBillingTypeOptions[ProductBillingType.Monthly]
				} Recurring`
			);
		case ProductBillingType.Quarterly:
			return (
				labels?.quarterly_recurring_label ??
				`${
					productBillingTypeOptions[ProductBillingType.Quarterly]
				} Recurring`
			);
		case ProductBillingType.SemiAnnually:
			return (
				labels?.semi_annually_recurring_label ??
				`${
					productBillingTypeOptions[ProductBillingType.SemiAnnually]
				} Recurring`
			);
		case ProductBillingType.Weekly:
			return (
				labels?.weekly_recurring_label ??
				`${
					productBillingTypeOptions[ProductBillingType.Weekly]
				} Recurring`
			);
		case ProductBillingType.OneTime:
		default:
			return "";
	}
};

export const getEditQuantityMessage = (quantityRange: EditQuantityRange) => {
	if (
		quantityRange.max_quantity !== null ||
		quantityRange.min_quantity !== null
	) {
		if (
			quantityRange.max_quantity !== null &&
			quantityRange.min_quantity !== null
		) {
			return `Enter value between ${quantityRange.min_quantity} and ${quantityRange.max_quantity}`;
		}
		if (quantityRange.max_quantity !== null)
			return `Value cannot be more than ${quantityRange.max_quantity}`;
		if (quantityRange.min_quantity !== null)
			return `Value cannot be less than ${quantityRange.min_quantity}`;
	}
	return "";
};

export const validateInputNumber = (
	value: string | number | null | undefined
) => {
	if (value === undefined || value === null) {
		return false;
	}
	const quantityValue = parseInt(value.toString());
	return !isNaN(quantityValue) && quantityValue < NumberLimit.NUMBER_MAX;
};
